import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@mui/styles';
import {
	Alert, Box, Button, Card, CardHeader, CardContent, CardActions, Grid, Divider,
	Typography, CircularProgress, colors
} from '@mui/material';
import clsx from 'clsx';
import { FilePicker } from 'components';

import { enqueueSnackbar } from 'actions';
import { ProcessAlarmsDiff } from '../../../actions/AlarmsDiffActions.js';

const useStyles = makeStyles((theme) => ({
	content: {
		marginTop: theme.spacing(3),
		border: `2px solid ${colors.orange[800]}`,
	},
	badge: {
		display: 'inline-block',
		padding: '2px 8px',
		borderRadius: 4,
		color: theme.palette.white,
		fontSize: '0.75rem',
		fontWeight: 600,
		letterSpacing: 0.5,
		marginRight: theme.spacing(1),
	},
	badgeColor: {
		backgroundColor: colors.orange[800],
	},
	cardHeaderTitle: {
		marginBottom: theme.spacing(1),
	},
	process: {
		color: theme.palette.white,
		backgroundColor: colors.orange[800],
		'&:hover': {
			backgroundColor: colors.orange[900],
		},
	},
	results: {
		marginTop: theme.spacing(3),
	},
	newAlarms: {
		color: colors.red[800],
	},
	noNewAlarms: {
		color: colors.green[800],
	},
}));

const MAX_FILE_SIZE = 100000000;

const AlarmsDiffForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();

	// This state is only used inside this card, so it does not go to the store.
	const [zipFile, setZipFile] = useState(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const [result, setResult] = useState(null);

	const _onChangeFile = (event) => {
		if (event.target.files && event.target.files.length > 0) {
			setZipFile(event.target.files[0]);
			// A new file means the counts on the screen are from the previous run.
			setResult(null);
		}
	};

	const _process = async () => {
		if (zipFile.size > MAX_FILE_SIZE) {
			dispatch(enqueueSnackbar({ message: 'File size cannot be superior to 100MB', options: { variant: 'warning' } }));
			return;
		}

		setIsProcessing(true);
		setResult(null);

		const diffResult = await dispatch(ProcessAlarmsDiff(zipFile));

		setResult(diffResult);
		setIsProcessing(false);
	};

	const hasNewAlarms = result && result.newAlarmCount > 0;

	return (
		<Card className={classes.content}>
			<CardHeader
				classes={{ title: classes.cardHeaderTitle }}
				title={
					<Box display="flex" alignItems="center">
						<span className={clsx(classes.badge, classes.badgeColor)}>NOKIA ALARMS</span>
						<Typography variant="body2"><strong>Pre/Post Diff</strong></Typography>
					</Box>
				}
				subheader="Compares the alarms taken before and after the change"
			/>
			<Divider />
			<CardContent>
				<Alert severity="info">
					Upload a zip with the two alarm exports taken with the WorkingSet file: one name
					ending in <strong>_pre.csv</strong> and one ending in <strong>_post.csv</strong>.
					The result is an Excel with three sheets: Pre, Post and New.
				</Alert>

				<Grid container spacing={3} className={classes.results}>
					<Grid item md={6} xs={12}>
						<FilePicker
							placeholder="Select the zip with the pre and post alarm CSVs"
							accept=".zip"
							value={zipFile?.name || ''}
							onChange={_onChangeFile}
							disabled={isProcessing}
						/>
					</Grid>
				</Grid>

				{result && (
					<Grid container spacing={3} className={classes.results}>
						<Grid item md={3} xs={6}>
							<Typography variant="body2" color="textSecondary">Pre alarms</Typography>
							<Typography variant="h5">{result.preAlarmCount}</Typography>
						</Grid>
						<Grid item md={3} xs={6}>
							<Typography variant="body2" color="textSecondary">Post alarms</Typography>
							<Typography variant="h5">{result.postAlarmCount}</Typography>
						</Grid>
						<Grid item md={3} xs={6}>
							<Typography variant="body2" color="textSecondary">New alarms</Typography>
							<Typography variant="h5" className={hasNewAlarms ? classes.newAlarms : classes.noNewAlarms}>
								{result.newAlarmCount}
							</Typography>
						</Grid>
						<Grid item md={3} xs={6}>
							<Typography variant="body2" color="textSecondary"></Typography>
							<Typography variant="body2"></Typography>
						</Grid>
					</Grid>
				)}
			</CardContent>
			<Divider />
			<CardActions>
				<Button
					className={classes.process}
					onClick={_process}
					variant="contained"
					disabled={!zipFile || isProcessing}
					startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : null}
				>
					{isProcessing ? 'Processing...' : 'Process Alarms Diff'}
				</Button>
			</CardActions>
		</Card>
	);
};

export default AlarmsDiffForm;
